import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  DollarSign,
  Download,
  Hash,
  Package,
  RefreshCw,
  TrendingUp,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import apiClient from '@/services/api';
import type { ItemsReport } from '@/types/api';
import { KPICard } from '@/components/reports/KPICard';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { Can } from '@/components/auth/Can';
import { PERM } from '@/constants/permissions';
import { buildMenuCsv, downloadCsv, printMenuReport } from '@/utils/reportExport';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

/** Default Menu-tab report: top sellers, category breakdown, modifier-level detail. */
export default function MenuInsightsReport({ filters }: ReportViewProps) {
  const {
    selectedRestaurantId,
    startDate,
    endDate,
    formatCurrency,
    selectedRestaurant,
  } = filters;
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleExpanded = (key: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const { data: itemsReport, isLoading, isError, refetch } = useQuery<ItemsReport>({
    queryKey: ['items-report', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getItemsReport(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const exportMeta = () => ({
    restaurantName: selectedRestaurant?.name ?? '',
    startDate,
    endDate,
    formatCurrency,
  });

  const handleExport = (exportFormat: 'PDF' | 'CSV') => {
    if (!itemsReport) return;
    if (exportFormat === 'CSV') {
      downloadCsv(`menu-report-${format(new Date(), 'yyyy-MM-dd')}.csv`, buildMenuCsv(itemsReport, exportMeta()));
    } else {
      printMenuReport(itemsReport, exportMeta());
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Menu Overview</h2>
          <p className="mt-1 text-sm text-gray-500">Top sellers, category breakdown, and modifier-level detail.</p>
        </div>
        <Can name={PERM.REPORT_EXPORT}>
          <div className="flex gap-2">
            <button
              onClick={() => handleExport('CSV')}
              className="btn btn-outline flex items-center gap-2"
              disabled={!itemsReport}
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
            <button
              onClick={() => handleExport('PDF')}
              className="btn btn-outline flex items-center gap-2"
              disabled={!itemsReport}
            >
              <Download className="h-4 w-4" />
              Export PDF
            </button>
          </div>
        </Can>
      </div>

      {!selectedRestaurantId ? (
        <div className="card">
          <div className="card-body text-center text-gray-500">
            Select a restaurant to view menu insights
          </div>
        </div>
      ) : isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : isError ? (
        <div className="card"><div className="card-body p-8 text-center">
          <AlertTriangle className="h-8 w-8 mx-auto text-red-500 mb-3" />
          <p className="text-gray-900 font-medium mb-3">Failed to load menu insights</p>
          <button onClick={() => refetch()} className="btn btn-outline h-10">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </button>
        </div></div>
      ) : itemsReport ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <KPICard
              label="Total Items Sold"
              value={Number(itemsReport.totalItemsSold).toLocaleString()}
              icon={Package}
              palette="blue"
            />
            <KPICard
              label="Total Items Revenue"
              value={formatCurrency(itemsReport.totalItemsRevenue)}
              icon={DollarSign}
              palette="green"
            />
            <KPICard
              label="Distinct Items"
              value={Number(itemsReport.distinctItemsCount).toLocaleString()}
              icon={Hash}
              palette="red"
            />
            <KPICard
              label="Avg Item Price"
              value={formatCurrency(itemsReport.averageItemPrice)}
              icon={TrendingUp}
              palette="purple"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card">
              <div className="card-body">
                <h2 className="text-lg font-semibold mb-4">Revenue by Category</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={itemsReport.revenueByCategory}
                      dataKey="revenue"
                      nameKey="category"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label
                    >
                      {itemsReport.revenueByCategory.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <h2 className="text-lg font-semibold mb-4">Items Sold by Hour</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={itemsReport.itemsSoldByHour}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="quantity" fill="#0088FE" name="Quantity" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h2 className="text-lg font-semibold mb-4">Top Selling Items</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="w-8 px-2 py-3"></th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Item Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity Sold
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Revenue
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {itemsReport.topSellingItems.map((item, index) => {
                      const key = `${item.name}-${index}`;
                      const hasSubitems = (item.subitems?.length ?? 0) > 0;
                      const isExpanded = expandedItems.has(key);
                      return (
                        <Fragment key={key}>
                          <tr
                            className={hasSubitems ? 'cursor-pointer hover:bg-gray-50' : ''}
                            onClick={() => hasSubitems && toggleExpanded(key)}
                          >
                            <td className="w-8 px-2 py-4 text-gray-400">
                              {hasSubitems &&
                                (isExpanded ? (
                                  <ChevronDown className="w-4 h-4" />
                                ) : (
                                  <ChevronRight className="w-4 h-4" />
                                ))}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {item.name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {Number(item.quantity).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatCurrency(item.revenue)}
                            </td>
                          </tr>
                          {hasSubitems &&
                            isExpanded &&
                            item.subitems!.map((sub, sIdx) => (
                              <tr key={`sub-${sIdx}`} className="bg-gray-50">
                                <td className="w-8 px-2 py-2"></td>
                                <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-700 pl-12">
                                  ↳ {sub.name}
                                </td>
                                <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">
                                  {Number(sub.quantity).toLocaleString()}
                                </td>
                                <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">
                                  {formatCurrency(sub.revenue)}
                                </td>
                              </tr>
                            ))}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body text-center text-gray-500">No data available</div>
        </div>
      )}
    </div>
  );
}
